// @ts-nocheck
/**
 * CompilationConsole — shows compilation output (stdout, stderr, errors, core install logs).
 * Rendered below the code editor when open. Similar to VS Code's output panel.
 */

import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CompilationLog,
  CompileTarget,
} from "@/lib/velxio/utils/compilationLogger";

/** One contiguous run of log lines for the same target (or none). */
interface LogGroup {
  target?: CompileTarget;
  entries: { log: CompilationLog; index: number }[];
}

/** Group consecutive log lines by target so the console renders a section per
 *  board/chip. Consecutive-run (not collapse-all) preserves chronological order
 *  — a leading "Compiling all targets" and a trailing "Done" stay where they
 *  are, around the per-target sections. */
function groupLogs(logs: CompilationLog[]): LogGroup[] {
  const groups: LogGroup[] = [];
  logs.forEach((log, index) => {
    const last = groups[groups.length - 1];
    if (last && (last.target?.id ?? null) === (log.target?.id ?? null)) {
      last.entries.push({ log, index });
    } else {
      groups.push({ target: log.target, entries: [{ log, index }] });
    }
  });
  return groups;
}

/** A target section's overall outcome, derived from its lines. */
function groupStatus(
  entries: LogGroup["entries"],
): "error" | "success" | "running" {
  if (entries.some((e) => e.log.type === "error")) return "error";
  if (entries.some((e) => e.log.type === "success")) return "success";
  return "running";
}

interface CompilationConsoleProps {
  isOpen: boolean;
  onClose: () => void;
  logs: CompilationLog[];
  onClear: () => void;
}

export const CompilationConsole: React.FC<CompilationConsoleProps> = ({
  isOpen,
  onClose,
  logs,
  onClear,
}) => {
  const { t } = useTranslation();
  const outputRef = useRef<HTMLDivElement>(null);
  const [autoscroll, setAutoscroll] = useState(true);
  const [filter, setFilter] = useState<"all" | "errors" | "warnings">("all");
  const prevLogsLenRef = useRef(0);

  useEffect(() => {
    if (autoscroll && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [logs, autoscroll]);

  // Auto-switch to "Errors" filter when a new batch of logs arrives with
  // errors — but reset to "all" the moment a fresh compile clears/shrinks
  // the log. Without that reset the 'errors' filter was sticky: after one
  // failing compile, every later SUCCESSFUL compile (info/success lines
  // only) was filtered out and the console looked empty while the sim
  // started. (Reported: "output doesn't refresh after the first compile,
  // unless there's an error".)
  useEffect(() => {
    if (logs.length < prevLogsLenRef.current) {
      // A clear() or reset shrank the list — treat it as a brand-new
      // compile and drop any sticky filter so the next batch is visible.
      prevLogsLenRef.current = logs.length;
      setFilter("all");
      return;
    }
    if (logs.length === prevLogsLenRef.current) return;
    const newLogs = logs.slice(prevLogsLenRef.current);
    prevLogsLenRef.current = logs.length;
    const hasNewErrors = newLogs.some((l) => l.type === "error");
    if (hasNewErrors) setFilter("errors");
  }, [logs]);

  const filteredLogs = logs.filter((log) => {
    // "hint" lines are the beginner-friendly explanations that accompany a
    // failure — always keep them visible in the error/warning views.
    if (filter === "errors")
      return log.type === "error" || log.type === "hint";
    if (filter === "warnings")
      return log.type === "warning" || log.type === "error" || log.type === "hint";
    return true;
  });

  const errorCount = logs.filter((l) => l.type === "error").length;
  const warningCount = logs.filter((l) => l.type === "warning").length;

  if (!isOpen) return null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.title}>{t("editor.console.title")}</span>
          <div style={styles.badges}>
            {errorCount > 0 && (
              <span
                style={styles.errorBadge}
                title={t("editor.console.errorCount", { count: errorCount })}
              >
                ✕ {errorCount}
              </span>
            )}
            {warningCount > 0 && (
              <span
                style={styles.warningBadge}
                title={t("editor.console.warningCount", {
                  count: warningCount,
                })}
              >
                ⚠ {warningCount}
              </span>
            )}
          </div>
        </div>
        <div style={styles.headerRight}>
          {/* Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            style={styles.filterSelect}
          >
            <option value="all">{t("editor.console.filterAll")}</option>
            <option value="errors">{t("editor.console.filterErrors")}</option>
            <option value="warnings">
              {t("editor.console.filterWarnings")}
            </option>
          </select>

          {/* Autoscroll */}
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={autoscroll}
              onChange={(e) => setAutoscroll(e.target.checked)}
              style={styles.checkbox}
            />
            {t("editor.console.auto")}
          </label>

          {/* Clear */}
          <button
            onClick={onClear}
            style={styles.iconBtn}
            title={t("editor.console.clear")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            style={styles.iconBtn}
            title={t("editor.console.close")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Output content — grouped into a section per board/chip target. */}
      <div ref={outputRef} style={styles.output}>
        {filteredLogs.length === 0 ? (
          <div style={styles.emptyState}>{t("editor.console.empty")}</div>
        ) : (
          groupLogs(filteredLogs).map((group, gi) =>
            group.target ? (
              <div key={`g-${gi}`} style={styles.targetGroup}>
                <div style={styles.targetHeader}>
                  <span
                    style={{
                      ...styles.targetStatus,
                      color: statusColor(groupStatus(group.entries)),
                    }}
                  >
                    {statusGlyph(groupStatus(group.entries))}
                  </span>
                  <span style={styles.targetLabel}>{group.target.label}</span>
                  <span style={styles.targetKind}>{group.target.kind}</span>
                </div>
                <div style={styles.targetBody}>
                  {group.entries.map(({ log, index }) => (
                    <LogLine key={index} log={log} />
                  ))}
                </div>
              </div>
            ) : (
              // No target — plain narration ("Compiling all targets...", "Done").
              group.entries.map(({ log, index }) => (
                <LogLine key={index} log={log} />
              ))
            ),
          )
        )}
      </div>
    </div>
  );
};

const LogLine: React.FC<{ log: CompilationLog }> = ({ log }) => {
  // "hint" lines are the beginner-friendly explanations — render them as a
  // highlighted callout block instead of a plain timestamped line.
  if (log.type === "hint") {
    return (
      <div style={styles.hintBlock}>
        <span style={styles.hintMessage}>{log.message}</span>
      </div>
    );
  }
  return (
    <div style={styles.logLine}>
      <span style={styles.timestamp}>
        {log.timestamp.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}
      </span>
      <span style={{ ...styles.logMessage, color: logColor(log.type) }}>
        {log.type === "core-install" && (
          <span style={styles.coreTag}>CORE </span>
        )}
        {log.message}
      </span>
    </div>
  );
};

function statusColor(status: "error" | "success" | "running"): string {
  return status === "error"
    ? "#ef5350"
    : status === "success"
      ? "#66bb6a"
      : "#9aa0a6";
}

function statusGlyph(status: "error" | "success" | "running"): string {
  return status === "error" ? "✕" : status === "success" ? "✓" : "▸";
}

function logColor(type: CompilationLog["type"]): string {
  switch (type) {
    case "error":
      return "#ef5350";
    case "warning":
      return "#ffa726";
    case "success":
      return "#66bb6a";
    case "core-install":
      return "#4fc3f7";
    case "hint":
      return "#ffd54f";
    default:
      return "#cccccc";
  }
}

// ── Inline styles (dark VS Code theme) ────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    background: "#1e1e1e",
    borderTop: "1px solid #333",
    fontSize: 12,
    fontFamily: "'Cascadia Code', 'Fira Code', Consolas, monospace",
    overflow: "hidden",
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 10px",
    background: "#252526",
    borderBottom: "1px solid #333",
    flexShrink: 0,
    minHeight: 30,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  title: {
    color: "#cccccc",
    fontWeight: 600,
    fontSize: 12,
    fontFamily: "system-ui, sans-serif",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  badges: {
    display: "flex",
    gap: 6,
  },
  errorBadge: {
    color: "#ef5350",
    background: "rgba(239, 83, 80, 0.15)",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 11,
    fontFamily: "system-ui, sans-serif",
  },
  warningBadge: {
    color: "#ffa726",
    background: "rgba(255, 167, 38, 0.15)",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 11,
    fontFamily: "system-ui, sans-serif",
  },
  filterSelect: {
    background: "#333",
    color: "#ccc",
    border: "1px solid #555",
    borderRadius: 3,
    fontSize: 11,
    padding: "2px 4px",
    cursor: "pointer",
    fontFamily: "system-ui, sans-serif",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    color: "#999",
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "system-ui, sans-serif",
  },
  checkbox: {
    accentColor: "#0e639c",
  },
  iconBtn: {
    background: "none",
    border: "none",
    color: "#999",
    cursor: "pointer",
    padding: "3px 4px",
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
  },
  output: {
    flex: 1,
    overflow: "auto",
    padding: "4px 10px",
    lineHeight: 1.6,
  },
  emptyState: {
    color: "#666",
    fontStyle: "italic",
    padding: "12px 0",
    fontFamily: "system-ui, sans-serif",
  },
  targetGroup: {
    marginTop: 6,
    borderLeft: "2px solid #3a3a3a",
    paddingLeft: 8,
  },
  targetHeader: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "1px 0 2px",
  },
  targetStatus: {
    fontWeight: 700,
    flexShrink: 0,
  },
  targetLabel: {
    color: "#e0e0e0",
    fontWeight: 700,
    fontSize: 11.5,
    fontFamily: "system-ui, sans-serif",
  },
  targetKind: {
    color: "#777",
    fontSize: 9,
    fontFamily: "system-ui, sans-serif",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    border: "1px solid #3a3a3a",
    borderRadius: 3,
    padding: "0 4px",
  },
  targetBody: {
    paddingLeft: 4,
  },
  logLine: {
    display: "flex",
    gap: 8,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-all" as const,
  },
  hintBlock: {
    margin: "6px 0",
    padding: "8px 10px",
    background: "rgba(255, 213, 79, 0.10)",
    border: "1px solid rgba(255, 213, 79, 0.35)",
    borderLeft: "3px solid #ffd54f",
    borderRadius: 4,
  },
  hintMessage: {
    color: "#f5e6b3",
    whiteSpace: "pre-wrap" as const,
    fontFamily: "system-ui, sans-serif",
    fontSize: 12,
    lineHeight: 1.5,
  },
  timestamp: {
    color: "#555",
    flexShrink: 0,
    userSelect: "none" as const,
  },
  logMessage: {
    flex: 1,
  },
  coreTag: {
    background: "rgba(79, 195, 247, 0.15)",
    color: "#4fc3f7",
    padding: "0 4px",
    borderRadius: 2,
    marginRight: 4,
    fontSize: 10,
    fontFamily: "system-ui, sans-serif",
  },
};
